import { motion } from 'framer-motion';
import { Clock, Loader2, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminService } from '@/services';
import type {
  AttendanceConfig,
  AttendanceConfigFormErrors,
  AttendanceConfigFormState,
  PenaltyConfigFormState,
  User,
} from '@/types';
import {
  extractErrorMessage,
  formatEmployeeId,
  formatTimeInput,
  parseIntegerString,
  validateIntegerString,
} from '@/utils';
import { cn } from '@/utils/cn';

export function Settings() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingPenalty, setSavingPenalty] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [attendanceConfig, setAttendanceConfig] = useState<AttendanceConfig | null>(null);
  const [globalConfig, setGlobalConfig] = useState<AttendanceConfigFormState>({
    monToFriClockInTime: '',
    gracePeriodMinutes: '',
    normalSatClockInTime: '',
    lastSatOfMonthClockInTime: '',
    clockOutRequired: false,
    lateFeesEnabled: false,
    roasterEnabled: false,
    salary: '',
    allowance: '',
  });
  const [penaltyConfig, setPenaltyConfig] = useState<PenaltyConfigFormState>({
    lateArrivalFee: '',
    extraLateFeePerInterval: '',
    extraLateFeeIntervalMinutes: '',
    missedClockOutFee: '',
    missedClockInFee: '',
  });
  const [errors, setErrors] = useState<AttendanceConfigFormErrors>({});

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) {
        toast.error('Invalid employee ID');
        navigate('/admin/staff');
        return;
      }

      setLoading(true);
      try {
        const staffList = await adminService.getStaffList({ search: id, limit: 1000 });
        const foundUser = staffList.users.find((u) => u.employeeId === Number.parseInt(id, 10));

        if (!foundUser) {
          toast.error('User not found');
          navigate('/admin/staff');
          return;
        }

        const userDetails = await adminService.getStaffDetails(foundUser._id);
        setUser(userDetails);
        setAttendanceConfig(userDetails.attendanceConfig);

        setGlobalConfig({
          monToFriClockInTime: userDetails.attendanceConfig.monToFriClockInTime,
          gracePeriodMinutes: String(userDetails.attendanceConfig.gracePeriodMinutes),
          normalSatClockInTime: userDetails.attendanceConfig.normalSatClockInTime,
          lastSatOfMonthClockInTime: userDetails.attendanceConfig.lastSatOfMonthClockInTime,
          clockOutRequired: userDetails.attendanceConfig.clockOutRequired,
          lateFeesEnabled: userDetails.attendanceConfig.lateFeesEnabled,
          roasterEnabled: userDetails.attendanceConfig.roasterEnabled ?? true,
          salary: String(userDetails.attendanceConfig.salary || 0),
          allowance: String(userDetails.attendanceConfig.allowance || 0),
        });

        setPenaltyConfig({
          lateArrivalFee: String(userDetails.attendanceConfig.lateArrivalFee),
          extraLateFeePerInterval: String(userDetails.attendanceConfig.extraLateFeePerInterval),
          extraLateFeeIntervalMinutes: String(
            userDetails.attendanceConfig.extraLateFeeIntervalMinutes,
          ),
          missedClockOutFee: String(userDetails.attendanceConfig.missedClockOutFee),
          missedClockInFee: String(userDetails.attendanceConfig.missedClockInFee || 0),
        });
      } catch (error) {
        toast.error('Failed to load user settings');
        navigate('/admin/staff');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, navigate]);

  const handleGlobalUpdate = async () => {
    if (!user || !attendanceConfig) return;

    setSavingGlobal(true);
    try {
      const updatedConfig: Partial<AttendanceConfig> = {
        monToFriClockInTime:
          globalConfig.monToFriClockInTime || attendanceConfig.monToFriClockInTime,
        gracePeriodMinutes:
          globalConfig.gracePeriodMinutes !== undefined &&
          globalConfig.gracePeriodMinutes.trim() !== ''
            ? parseIntegerString(globalConfig.gracePeriodMinutes)
            : attendanceConfig.gracePeriodMinutes,
        normalSatClockInTime:
          globalConfig.normalSatClockInTime || attendanceConfig.normalSatClockInTime,
        lastSatOfMonthClockInTime:
          globalConfig.lastSatOfMonthClockInTime || attendanceConfig.lastSatOfMonthClockInTime,
        clockOutRequired: globalConfig.clockOutRequired ?? attendanceConfig.clockOutRequired,
        lateFeesEnabled: globalConfig.lateFeesEnabled ?? attendanceConfig.lateFeesEnabled,
        roasterEnabled: globalConfig.roasterEnabled ?? attendanceConfig.roasterEnabled ?? true,
        salary:
          globalConfig.salary !== undefined && globalConfig.salary.trim() !== ''
            ? parseIntegerString(globalConfig.salary)
            : attendanceConfig.salary || 0,
        allowance:
          globalConfig.allowance !== undefined && globalConfig.allowance.trim() !== ''
            ? parseIntegerString(globalConfig.allowance)
            : attendanceConfig.allowance || 0,
        lateArrivalFee: attendanceConfig.lateArrivalFee,
        extraLateFeePerInterval: attendanceConfig.extraLateFeePerInterval,
        extraLateFeeIntervalMinutes: attendanceConfig.extraLateFeeIntervalMinutes,
        missedClockOutFee: attendanceConfig.missedClockOutFee,
        missedClockInFee: attendanceConfig.missedClockInFee || 0,
      };

      const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
      if (updatedConfig.monToFriClockInTime && !timeRegex.test(updatedConfig.monToFriClockInTime)) {
        toast.error('Invalid Mon-Fri clock-in time format. Use HH:mm');
        setSavingGlobal(false);
        return;
      }
      if (
        updatedConfig.normalSatClockInTime &&
        !timeRegex.test(updatedConfig.normalSatClockInTime)
      ) {
        toast.error('Invalid Normal Saturday clock-in time format. Use HH:mm');
        setSavingGlobal(false);
        return;
      }
      if (
        updatedConfig.lastSatOfMonthClockInTime &&
        !timeRegex.test(updatedConfig.lastSatOfMonthClockInTime)
      ) {
        toast.error('Invalid Last Saturday clock-in time format. Use HH:mm');
        setSavingGlobal(false);
        return;
      }

      await adminService.updateAttendanceConfig(user._id, updatedConfig);
      toast.success('Global attendance settings updated successfully!');

      const userDetails = await adminService.getStaffDetails(user._id);
      setUser(userDetails);
      setAttendanceConfig(userDetails.attendanceConfig);

      setGlobalConfig({
        monToFriClockInTime: userDetails.attendanceConfig.monToFriClockInTime,
        gracePeriodMinutes: String(userDetails.attendanceConfig.gracePeriodMinutes),
        normalSatClockInTime: userDetails.attendanceConfig.normalSatClockInTime,
        lastSatOfMonthClockInTime: userDetails.attendanceConfig.lastSatOfMonthClockInTime,
        clockOutRequired: userDetails.attendanceConfig.clockOutRequired,
        lateFeesEnabled: userDetails.attendanceConfig.lateFeesEnabled,
        roasterEnabled: userDetails.attendanceConfig.roasterEnabled ?? true,
        salary: String(userDetails.attendanceConfig.salary || 0),
        allowance: String(userDetails.attendanceConfig.allowance || 0),
      });
      setErrors({
        ...errors,
        gracePeriodMinutes: undefined,
        salary: undefined,
        allowance: undefined,
      });
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setSavingGlobal(false);
    }
  };

  const handlePenaltyUpdate = async () => {
    if (!user || !attendanceConfig) return;

    setSavingPenalty(true);
    try {
      const updatedConfig: Partial<AttendanceConfig> = {
        monToFriClockInTime: attendanceConfig.monToFriClockInTime,
        gracePeriodMinutes: attendanceConfig.gracePeriodMinutes,
        normalSatClockInTime: attendanceConfig.normalSatClockInTime,
        lastSatOfMonthClockInTime: attendanceConfig.lastSatOfMonthClockInTime,
        clockOutRequired: attendanceConfig.clockOutRequired,
        lateFeesEnabled: attendanceConfig.lateFeesEnabled,
        lateArrivalFee:
          penaltyConfig.lateArrivalFee !== undefined && penaltyConfig.lateArrivalFee.trim() !== ''
            ? parseIntegerString(penaltyConfig.lateArrivalFee)
            : attendanceConfig.lateArrivalFee,
        extraLateFeePerInterval:
          penaltyConfig.extraLateFeePerInterval !== undefined &&
          penaltyConfig.extraLateFeePerInterval.trim() !== ''
            ? parseIntegerString(penaltyConfig.extraLateFeePerInterval)
            : attendanceConfig.extraLateFeePerInterval,
        extraLateFeeIntervalMinutes:
          penaltyConfig.extraLateFeeIntervalMinutes !== undefined &&
          penaltyConfig.extraLateFeeIntervalMinutes.trim() !== ''
            ? parseIntegerString(penaltyConfig.extraLateFeeIntervalMinutes)
            : attendanceConfig.extraLateFeeIntervalMinutes,
        missedClockOutFee:
          penaltyConfig.missedClockOutFee !== undefined &&
          penaltyConfig.missedClockOutFee.trim() !== ''
            ? parseIntegerString(penaltyConfig.missedClockOutFee)
            : attendanceConfig.missedClockOutFee,
        missedClockInFee:
          penaltyConfig.missedClockInFee !== undefined &&
          penaltyConfig.missedClockInFee.trim() !== ''
            ? parseIntegerString(penaltyConfig.missedClockInFee)
            : attendanceConfig.missedClockInFee || 0,
      };

      await adminService.updateAttendanceConfig(user._id, updatedConfig);
      toast.success('Penalty rates updated successfully!');

      const userDetails = await adminService.getStaffDetails(user._id);
      setUser(userDetails);
      setAttendanceConfig(userDetails.attendanceConfig);

      setPenaltyConfig({
        lateArrivalFee: String(userDetails.attendanceConfig.lateArrivalFee),
        extraLateFeePerInterval: String(userDetails.attendanceConfig.extraLateFeePerInterval),
        extraLateFeeIntervalMinutes: String(
          userDetails.attendanceConfig.extraLateFeeIntervalMinutes,
        ),
        missedClockOutFee: String(userDetails.attendanceConfig.missedClockOutFee),
        missedClockInFee: String(userDetails.attendanceConfig.missedClockInFee || 0),
      });
      setErrors({
        lateArrivalFee: undefined,
        extraLateFeePerInterval: undefined,
        extraLateFeeIntervalMinutes: undefined,
        missedClockOutFee: undefined,
        missedClockInFee: undefined,
      });
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setSavingPenalty(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="w-12 h-12 text-neolab-green animate-spin" />
      </div>
    );
  }

  if (!user || !attendanceConfig) {
    return null;
  }

  const hasAnyError =
    !!errors.gracePeriodMinutes ||
    !!errors.lateArrivalFee ||
    !!errors.extraLateFeePerInterval ||
    !!errors.extraLateFeeIntervalMinutes ||
    !!errors.missedClockOutFee ||
    !!errors.missedClockInFee ||
    !!errors.salary ||
    !!errors.allowance;

  return (
    <div className="space-y-8">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Attendance Settings
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-2 font-medium">
              Configure attendance parameters for{' '}
              <span className="font-black text-neolab-green">
                {user.profile.firstName} {user.profile.lastName} (
                {formatEmployeeId(user.employeeId)})
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/staff')}
            className="px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
          >
            Back to Staff
          </button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm p-6"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 text-neolab-green">
              <Clock className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-black text-slate-900 dark:text-white">Global Attendance</h3>
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <label
                htmlFor="monToFriClockInTime"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Mon-Fri Clock-In Time
              </label>
              <input
                id="monToFriClockInTime"
                type="text"
                value={globalConfig.monToFriClockInTime || ''}
                onChange={(e) => {
                  const formatted = formatTimeInput(e.target.value);
                  setGlobalConfig({ ...globalConfig, monToFriClockInTime: formatted });
                }}
                placeholder="08:00"
                className="w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-black text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label
                htmlFor="gracePeriodMinutes"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Grace Period (Minutes)
              </label>
              <input
                id="gracePeriodMinutes"
                type="text"
                value={String(globalConfig.gracePeriodMinutes ?? '')}
                onChange={(e) => {
                  const value = e.target.value;
                  const validation = validateIntegerString(value);
                  setGlobalConfig({
                    ...globalConfig,
                    gracePeriodMinutes: value,
                  });
                  setErrors({
                    ...errors,
                    gracePeriodMinutes: validation.isValid ? undefined : validation.error,
                  });
                }}
                className={cn(
                  'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white',
                  errors.gracePeriodMinutes
                    ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                    : 'focus:ring-neolab-green/20',
                )}
              />
              {errors.gracePeriodMinutes && (
                <p className="text-xs font-bold text-rose-500 mt-1">{errors.gracePeriodMinutes}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="normalSatClockInTime"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Normal Saturday Clock-In Time
              </label>
              <input
                id="normalSatClockInTime"
                type="text"
                value={globalConfig.normalSatClockInTime || ''}
                onChange={(e) => {
                  const formatted = formatTimeInput(e.target.value);
                  setGlobalConfig({ ...globalConfig, normalSatClockInTime: formatted });
                }}
                placeholder="08:30"
                className="w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-black text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label
                htmlFor="lastSatOfMonthClockInTime"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Last Saturday Clock-In Time
              </label>
              <input
                id="lastSatOfMonthClockInTime"
                type="text"
                value={globalConfig.lastSatOfMonthClockInTime || ''}
                onChange={(e) => {
                  const formatted = formatTimeInput(e.target.value);
                  setGlobalConfig({ ...globalConfig, lastSatOfMonthClockInTime: formatted });
                }}
                placeholder="10:00"
                className="w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-black text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label
                htmlFor="clockOutRequired"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Clock-Out Required
              </label>
              <select
                id="clockOutRequired"
                value={globalConfig.clockOutRequired ? 'true' : 'false'}
                onChange={(e) =>
                  setGlobalConfig({ ...globalConfig, clockOutRequired: e.target.value === 'true' })
                }
                className="w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-black text-slate-900 dark:text-white"
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="lateFeesEnabled"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Late Fees Enabled
              </label>
              <select
                id="lateFeesEnabled"
                value={globalConfig.lateFeesEnabled ? 'true' : 'false'}
                onChange={(e) =>
                  setGlobalConfig({ ...globalConfig, lateFeesEnabled: e.target.value === 'true' })
                }
                className="w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-black text-slate-900 dark:text-white"
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="roasterEnabled"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Roaster Enabled
              </label>
              <select
                id="roasterEnabled"
                value={globalConfig.roasterEnabled ? 'true' : 'false'}
                onChange={(e) =>
                  setGlobalConfig({ ...globalConfig, roasterEnabled: e.target.value === 'true' })
                }
                className="w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-black text-slate-900 dark:text-white"
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="salary"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Salary (₦)
              </label>
              <input
                id="salary"
                type="text"
                value={String(globalConfig.salary ?? '')}
                onChange={(e) => {
                  const value = e.target.value;
                  const validation = validateIntegerString(value);
                  setGlobalConfig({
                    ...globalConfig,
                    salary: value,
                  });
                  setErrors({
                    ...errors,
                    salary: validation.isValid ? undefined : validation.error,
                  });
                }}
                className={cn(
                  'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white',
                  errors.salary
                    ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                    : 'focus:ring-neolab-green/20',
                )}
              />
              {errors.salary && (
                <p className="text-xs font-bold text-rose-500 mt-1">{errors.salary}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="allowance"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Allowance (₦)
              </label>
              <input
                id="allowance"
                type="text"
                value={String(globalConfig.allowance ?? '')}
                onChange={(e) => {
                  const value = e.target.value;
                  const validation = validateIntegerString(value);
                  setGlobalConfig({
                    ...globalConfig,
                    allowance: value,
                  });
                  setErrors({
                    ...errors,
                    allowance: validation.isValid ? undefined : validation.error,
                  });
                }}
                className={cn(
                  'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white',
                  errors.allowance
                    ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                    : 'focus:ring-neolab-green/20',
                )}
              />
              {errors.allowance && (
                <p className="text-xs font-bold text-rose-500 mt-1">{errors.allowance}</p>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleGlobalUpdate}
            disabled={savingGlobal || savingPenalty || hasAnyError}
            className={cn(
              'w-full px-6 py-4 rounded-xl bg-neolab-green text-white font-black text-sm shadow-lg shadow-green-500/20 hover:bg-neolab-green/90 transition-all cursor-pointer',
              (savingGlobal || savingPenalty || hasAnyError) && 'opacity-50 cursor-not-allowed',
            )}
          >
            {savingGlobal ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Updating...
              </span>
            ) : (
              'Update'
            )}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm p-6"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 rounded-xl bg-rose-500/5 dark:bg-rose-500/10 text-rose-500">
              <Wallet className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-black text-slate-900 dark:text-white">Penalty Rates</h3>
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <label
                htmlFor="lateArrivalFee"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Late Arrival Fee (₦)
              </label>
              <input
                id="lateArrivalFee"
                type="text"
                value={String(penaltyConfig.lateArrivalFee ?? '')}
                onChange={(e) => {
                  const value = e.target.value;
                  const validation = validateIntegerString(value);
                  setPenaltyConfig({
                    ...penaltyConfig,
                    lateArrivalFee: value,
                  });
                  setErrors({
                    ...errors,
                    lateArrivalFee: validation.isValid ? undefined : validation.error,
                  });
                }}
                className={cn(
                  'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white',
                  errors.lateArrivalFee
                    ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                    : 'focus:ring-neolab-green/20',
                )}
              />
              {errors.lateArrivalFee && (
                <p className="text-xs font-bold text-rose-500 mt-1">{errors.lateArrivalFee}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="extraLateFeePerInterval"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Extra Late Fee Per Interval (₦)
              </label>
              <input
                id="extraLateFeePerInterval"
                type="text"
                value={String(penaltyConfig.extraLateFeePerInterval ?? '')}
                onChange={(e) => {
                  const value = e.target.value;
                  const validation = validateIntegerString(value);
                  setPenaltyConfig({
                    ...penaltyConfig,
                    extraLateFeePerInterval: value,
                  });
                  setErrors({
                    ...errors,
                    extraLateFeePerInterval: validation.isValid ? undefined : validation.error,
                  });
                }}
                className={cn(
                  'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white',
                  errors.extraLateFeePerInterval
                    ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                    : 'focus:ring-neolab-green/20',
                )}
              />
              {errors.extraLateFeePerInterval && (
                <p className="text-xs font-bold text-rose-500 mt-1">
                  {errors.extraLateFeePerInterval}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="extraLateFeeIntervalMinutes"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Extra Late Fee Interval (Minutes)
              </label>
              <input
                id="extraLateFeeIntervalMinutes"
                type="text"
                value={String(penaltyConfig.extraLateFeeIntervalMinutes ?? '')}
                onChange={(e) => {
                  const value = e.target.value;
                  const validation = validateIntegerString(value);
                  setPenaltyConfig({
                    ...penaltyConfig,
                    extraLateFeeIntervalMinutes: value,
                  });
                  setErrors({
                    ...errors,
                    extraLateFeeIntervalMinutes: validation.isValid ? undefined : validation.error,
                  });
                }}
                className={cn(
                  'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white',
                  errors.extraLateFeeIntervalMinutes
                    ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                    : 'focus:ring-neolab-green/20',
                )}
              />
              {errors.extraLateFeeIntervalMinutes && (
                <p className="text-xs font-bold text-rose-500 mt-1">
                  {errors.extraLateFeeIntervalMinutes}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="missedClockOutFee"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Missed Clock-Out Fee (₦)
              </label>
              <input
                id="missedClockOutFee"
                type="text"
                value={String(penaltyConfig.missedClockOutFee ?? '')}
                onChange={(e) => {
                  const value = e.target.value;
                  const validation = validateIntegerString(value);
                  setPenaltyConfig({
                    ...penaltyConfig,
                    missedClockOutFee: value,
                  });
                  setErrors({
                    ...errors,
                    missedClockOutFee: validation.isValid ? undefined : validation.error,
                  });
                }}
                className={cn(
                  'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white',
                  errors.missedClockOutFee
                    ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                    : 'focus:ring-neolab-green/20',
                )}
              />
              {errors.missedClockOutFee && (
                <p className="text-xs font-bold text-rose-500 mt-1">{errors.missedClockOutFee}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="missedClockInFee"
                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block"
              >
                Missed Clock-In Fee (₦)
              </label>
              <input
                id="missedClockInFee"
                type="text"
                value={String(penaltyConfig.missedClockInFee ?? '')}
                onChange={(e) => {
                  const value = e.target.value;
                  const validation = validateIntegerString(value);
                  setPenaltyConfig({
                    ...penaltyConfig,
                    missedClockInFee: value,
                  });
                  setErrors({
                    ...errors,
                    missedClockInFee: validation.isValid ? undefined : validation.error,
                  });
                }}
                className={cn(
                  'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white',
                  errors.missedClockInFee
                    ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                    : 'focus:ring-neolab-green/20',
                )}
              />
              {errors.missedClockInFee && (
                <p className="text-xs font-bold text-rose-500 mt-1">{errors.missedClockInFee}</p>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handlePenaltyUpdate}
            disabled={savingGlobal || savingPenalty || hasAnyError}
            className={cn(
              'w-full px-6 py-4 rounded-xl bg-rose-500 text-white font-black text-sm shadow-lg shadow-rose-500/20 hover:bg-rose-600 transition-all cursor-pointer',
              (savingGlobal || savingPenalty || hasAnyError) && 'opacity-50 cursor-not-allowed',
            )}
          >
            {savingPenalty ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Updating...
              </span>
            ) : (
              'Update'
            )}
          </button>
        </motion.div>
      </div>
    </div>
  );
}

export default Settings;
